/**
 * Accessibility Widget for Shopify - PERFORMANCE OPTIMIZED
 * WCAG 2.1 AA/AAA Compliant
 * Version: 4.0.0 (Lazy Load Edition)
 *
 * Key performance improvements over v3.1.0:
 * - Initial load: ~2KB of CSS instead of ~15KB (only FAB button styles)
 * - No DOM wrapping on init (saves massive reflow on first paint)
 * - Panel HTML built only on first open (saves ~5KB of innerHTML parsing)
 * - Loaded with requestIdleCallback - never blocks LCP
 * - mousemove listener only attached when reading mask is active
 * - Image tooltips use one-time scan, not repeated queries
 *
 * Installation (Shopify):
 * 1. Online Store > Themes > ••• > Edit code
 * 2. Upload to Assets, replace existing accessibility-widget.js
 * 3. In theme.liquid, ensure tag is:
 *    <script src="{{ 'accessibility-widget.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const CONFIG = {
    position: 'bottom-right',
    primaryColor: '#2563EB',
    storageKey: 'a11y_prefs',
    persistKey: 'a11y_persist',
  };

  const DEFAULTS = {
    fontSize: 100,
    contrast: 'normal',
    lineHeight: 1.5,
    letterSpacing: 0,
    zoom: 100,
    textAlign: null,
    cursor: false,
    links: false,
    images: false,
    animations: false,
    dyslexia: false,
    readingMask: false,
    focus: false,
    tooltips: false,
    open: false,
  };

  let state = Object.assign({}, DEFAULTS);
  let fullyLoaded = false;       // true once panel + full CSS injected
  let pageWrapped = false;       // true once #a11y-page-wrap exists
  let mouseMoveAttached = false; // tracks reading-mask listener

  // ─── Persistence ─────────────────────────────────────────
  function getPersist() {
    try { return localStorage.getItem(CONFIG.persistKey) === 'true'; } catch { return false; }
  }
  function setPersist(val) {
    try { localStorage.setItem(CONFIG.persistKey, val ? 'true' : 'false'); } catch {}
  }
  function saveState() {
    if (!getPersist()) return;
    try {
      const toSave = Object.assign({}, state);
      delete toSave.open;
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(toSave));
    } catch {}
  }
  function loadState() {
    if (!getPersist()) return;
    try {
      const saved = JSON.parse(localStorage.getItem(CONFIG.storageKey) || '{}');
      Object.assign(state, saved);
    } catch {}
  }
  function hasSavedState() {
    if (!getPersist()) return false;
    try {
      const saved = localStorage.getItem(CONFIG.storageKey);
      return saved && saved !== '{}';
    } catch { return false; }
  }

  // ─── MINIMAL CSS (loaded immediately) ────────────────────
  // Only enough to render the FAB button. Everything else
  // is injected later, on first open.
  const pos = CONFIG.position === 'bottom-left';
  const MINIMAL_CSS = `
    #a11y-toggle {
      position: fixed;
      ${pos ? 'left: 20px;' : 'right: 20px;'}
      bottom: 20px;
      width: 56px; height: 56px; border-radius: 50%;
      background: ${CONFIG.primaryColor}; border: none; cursor: pointer;
      z-index: 2147483645;
      box-shadow: 0 4px 20px rgba(37,99,235,.45);
      display: flex; align-items: center; justify-content: center;
      color: white; padding: 0;
      transition: transform .2s, box-shadow .2s, background .2s;
    }
    #a11y-toggle:hover { transform: scale(1.1); background: #1d4ed8; }
    #a11y-toggle:focus-visible { outline: 3px solid #fbbf24; outline-offset: 3px; }
    #a11y-toggle svg { pointer-events: none; }
    #a11y-skip-link {
      position: fixed; top: -100px; left: 50%;
      transform: translateX(-50%);
      background: ${CONFIG.primaryColor}; color: #fff;
      padding: 12px 28px; border-radius: 0 0 10px 10px;
      font-weight: 700; font-size: 16px; z-index: 2147483646;
      text-decoration: none; transition: top .2s;
      font-family: -apple-system, 'Segoe UI', Tahoma, sans-serif;
    }
    #a11y-skip-link:focus { top: 0; }
    @media (max-width: 600px) {
      #a11y-toggle {
        right: 16px !important; left: auto !important;
        bottom: 16px; width: 52px; height: 52px;
      }
    }
    @media (hover: none) {
      #a11y-toggle:hover { transform: none; box-shadow: 0 4px 20px rgba(37,99,235,.45); }
    }
  `;

  // ─── FULL CSS (loaded on first open) ─────────────────────
  function getFullCSS() {
    return `
      :root {
        --a11y-primary: ${CONFIG.primaryColor};
        --a11y-primary-dark: #1d4ed8;
        --a11y-bg: #ffffff;
        --a11y-surface: #f8fafc;
        --a11y-border: #e2e8f0;
        --a11y-text: #1e293b;
        --a11y-muted: #64748b;
        --a11y-radius: 14px;
        --a11y-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08);
      }

      body.a11y-contrast-high     #a11y-page-wrap { filter: contrast(1.6) saturate(1.3) !important; }
      body.a11y-contrast-inverted #a11y-page-wrap { filter: invert(1) hue-rotate(180deg) !important; }
      body.a11y-contrast-dark     #a11y-page-wrap { filter: brightness(0.2) contrast(1.2) !important; }
      body.a11y-contrast-yellow   #a11y-page-wrap { filter: grayscale(1) invert(1) !important; }

      #a11y-panel {
        position: fixed;
        ${pos ? 'left: 20px;' : 'right: 20px;'}
        bottom: 86px; width: 340px; max-height: 82vh;
        background: var(--a11y-bg); border-radius: var(--a11y-radius);
        box-shadow: var(--a11y-shadow); z-index: 2147483645;
        overflow: hidden; display: none; flex-direction: column;
        border: 1px solid var(--a11y-border);
        font-family: -apple-system, 'Segoe UI', Tahoma, sans-serif;
        font-size: 14px; color: var(--a11y-text);
      }
      #a11y-panel.open { display: flex; animation: a11yIn .25s cubic-bezier(.16,1,.3,1) forwards; }
      @keyframes a11yIn { from { opacity:0; transform: translateY(12px) scale(.97); } to { opacity:1; transform: none; } }

      .a11y-header { background: var(--a11y-primary); color: white; padding: 14px 16px; display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
      .a11y-header h2 { margin: 0; font-size: 15px; font-weight: 700; flex: 1; color: white; }
      .a11y-header-btns { display: flex; gap: 6px; }
      .a11y-header-btns button { background: rgba(255,255,255,.2); border: none; color: white; border-radius: 50%; width: 32px; height: 32px; padding: 0; cursor: pointer; transition: background .15s; display: inline-flex; align-items: center; justify-content: center; }
      .a11y-header-btns button:hover { background: rgba(255,255,255,.35); }
      .a11y-header-btns button:focus-visible { outline: 2px solid #fbbf24; outline-offset: 2px; }
      .a11y-header-btns button svg { width: 16px; height: 16px; display: block; }

      .a11y-body { overflow-y: auto; padding: 10px; flex: 1; scrollbar-width: thin; }
      .a11y-section { margin-bottom: 10px; }
      .a11y-section-label { font-size: 10px; font-weight: 700; color: var(--a11y-muted); text-transform: uppercase; letter-spacing: .08em; padding: 0 2px; margin-bottom: 5px; }

      .a11y-font-row { display: flex; gap: 4px; }
      .a11y-choice-btn { flex: 1; padding: 8px 4px; border: 1.5px solid var(--a11y-border); border-radius: 8px; background: var(--a11y-surface); cursor: pointer; font-weight: 700; color: var(--a11y-text); transition: all .15s; text-align: center; font-family: inherit; }
      .a11y-choice-btn:hover { border-color: var(--a11y-primary); color: var(--a11y-primary); background: #eff6ff; }
      .a11y-choice-btn.active { border-color: var(--a11y-primary); background: var(--a11y-primary); color: white; }

      .a11y-contrast-row { display: flex; gap: 4px; flex-wrap: wrap; }
      .a11y-contrast-btn { flex: 1; min-width: 56px; padding: 8px 4px; border: 2px solid transparent; border-radius: 8px; cursor: pointer; font-size: 11px; font-weight: 700; transition: all .15s; text-align: center; }
      .a11y-contrast-btn.active { border-color: #fbbf24 !important; outline: 2px solid #fbbf24; }

      .a11y-slider-row { display: flex; align-items: center; gap: 8px; padding: 3px 0; }
      .a11y-slider-label { font-size: 12px; font-weight: 600; color: var(--a11y-text); min-width: 100px; }
      .a11y-slider-row input[type=range] { flex: 1; accent-color: var(--a11y-primary); }

      .a11y-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; }
      .a11y-btn { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 11px 5px; border: 1.5px solid var(--a11y-border); border-radius: 10px; background: var(--a11y-surface); cursor: pointer; font-size: 11px; font-weight: 600; color: var(--a11y-text); transition: all .15s; text-align: center; line-height: 1.3; font-family: inherit; }
      .a11y-btn:hover { border-color: var(--a11y-primary); background: #eff6ff; color: var(--a11y-primary); }
      .a11y-btn.active { border-color: var(--a11y-primary); background: var(--a11y-primary); color: white; }
      .a11y-icon { width: 20px; height: 20px; display: block; flex-shrink: 0; }

      .a11y-align-btn { display: flex; align-items: center; justify-content: center; padding: 9px 4px; }
      .a11y-align-btn svg { width: 20px; height: 20px; display: block; }

      .a11y-persist-row { display: flex; align-items: center; padding: 9px 12px; background: #f0f9ff; border: 1.5px solid #bae6fd; border-radius: 9px; gap: 10px; }
      .a11y-persist-icon { width: 18px; height: 18px; color: #0369a1; flex-shrink: 0; }
      .a11y-persist-row span { font-size: 12px; font-weight: 600; color: #0369a1; flex: 1; }
      .a11y-toggle-switch { position: relative; width: 40px; height: 22px; flex-shrink: 0; }
      .a11y-toggle-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
      .a11y-toggle-switch .sw-track { display: block; width: 40px; height: 22px; background: #cbd5e1; border-radius: 22px; cursor: pointer; transition: background .2s; position: relative; }
      .a11y-toggle-switch .sw-track::before { content: ''; position: absolute; width: 16px; height: 16px; border-radius: 50%; background: white; left: 3px; top: 3px; transition: transform .2s; box-shadow: 0 1px 4px rgba(0,0,0,.2); }
      .a11y-toggle-switch input:checked + .sw-track { background: var(--a11y-primary); }
      .a11y-toggle-switch input:checked + .sw-track::before { transform: translateX(18px); }
      .a11y-toggle-switch input:focus-visible + .sw-track { outline: 2px solid #fbbf24; outline-offset: 2px; }

      .a11y-footer { padding: 9px 14px; border-top: 1px solid var(--a11y-border); display: flex; justify-content: center; flex-shrink: 0; }
      .a11y-declaration-link { font-size: 12px; color: var(--a11y-primary); text-decoration: none; font-weight: 600; }
      .a11y-declaration-link:hover { text-decoration: underline; }

      #a11y-reading-mask { position: fixed; inset: 0; pointer-events: none; z-index: 2147483643; display: none; }
      #a11y-reading-mask.active { display: block; }
      #a11y-mask-top, #a11y-mask-bottom { position: absolute; left: 0; right: 0; background: rgba(0,0,0,.65); }

      body.a11y-focus-highlight *:focus { outline: 3px solid #f59e0b !important; outline-offset: 3px !important; border-radius: 3px; }
      body.a11y-big-cursor, body.a11y-big-cursor * { cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Cpath d='M5 3l26 17-11 2-7 12z' fill='black' stroke='white' stroke-width='2'/%3E%3C/svg%3E") 5 3, auto !important; }
      body.a11y-highlight-links a { background: #fef08a !important; color: #1e1b4b !important; text-decoration: underline !important; border-radius: 3px; padding: 1px 2px; }
      body.a11y-hide-images img, body.a11y-hide-images picture { visibility: hidden !important; }
      body.a11y-no-animations *, body.a11y-no-animations *::before, body.a11y-no-animations *::after { animation-duration: .001ms !important; transition-duration: .001ms !important; }
      body.a11y-dyslexia * { font-family: 'Comic Sans MS', Arial, sans-serif !important; letter-spacing: .05em !important; word-spacing: .12em !important; }

      @media (max-width: 600px) {
        #a11y-panel {
          left: 0 !important; right: 0 !important; bottom: 0 !important;
          width: 100% !important; max-height: 88vh;
          border-radius: 20px 20px 0 0;
          border-left: none; border-right: none; border-bottom: none;
        }
        #a11y-panel::before {
          content: ''; display: block;
          width: 40px; height: 4px; background: #cbd5e1; border-radius: 4px;
          margin: 10px auto 0; flex-shrink: 0;
        }
        #a11y-panel.open { animation: a11ySlideUp .3s cubic-bezier(.16,1,.3,1) forwards; }
        @keyframes a11ySlideUp { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
        #a11y-backdrop {
          display: none; position: fixed; inset: 0;
          background: rgba(0,0,0,.4); z-index: 2147483644;
          backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);
        }
        #a11y-backdrop.visible { display: block; animation: a11yFadeIn .25s ease forwards; }
        @keyframes a11yFadeIn { from { opacity:0; } to { opacity:1; } }
        .a11y-btn       { padding: 13px 6px; font-size: 12px; }
        .a11y-choice-btn { padding: 11px 4px; }
        .a11y-contrast-btn { padding: 11px 4px; }
        .a11y-slider-row input[type=range] { height: 28px; }
        .a11y-header-btns button { width: 36px; height: 36px; }
        .a11y-header-btns button svg { width: 18px; height: 18px; }
        .a11y-grid { grid-template-columns: 1fr 1fr 1fr; }
        .a11y-icon { width: 22px; height: 22px; }
      }
      @media (hover: none) {
        .a11y-btn:hover, .a11y-choice-btn:hover, .a11y-contrast-btn:hover {
          border-color: var(--a11y-border); background: var(--a11y-surface); color: var(--a11y-text);
        }
      }
    `;
  }

  // ─── Panel HTML (built only on first open) ───────────────
  function buildPanelHTML() {
    return `
      <div id="a11y-backdrop" aria-hidden="true"></div>

      <div id="a11y-reading-mask" role="presentation" aria-hidden="true">
        <div id="a11y-mask-top"></div>
        <div id="a11y-mask-bottom"></div>
      </div>

      <div id="a11y-panel" role="dialog" aria-modal="false" aria-label="Accessibility Settings">
        <div class="a11y-header">
          <h2>Accessibility Settings</h2>
          <div class="a11y-header-btns">
            <button id="a11y-reset" aria-label="Reset all settings" title="Reset">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            </button>
            <button id="a11y-close" aria-label="Close panel" title="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <div class="a11y-body">
          <div class="a11y-section">
            <div class="a11y-persist-row">
              <svg class="a11y-persist-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              <span>Keep settings after page refresh</span>
              <label class="a11y-toggle-switch" aria-label="Keep settings after page refresh">
                <input type="checkbox" id="a11y-persist-chk">
                <span class="sw-track"></span>
              </label>
            </div>
          </div>

          <div class="a11y-section">
            <div class="a11y-section-label">Font Size</div>
            <div class="a11y-font-row">
              <button class="a11y-choice-btn" data-font="90"  style="font-size:11px" aria-label="Small font">A</button>
              <button class="a11y-choice-btn" data-font="100" style="font-size:14px" aria-label="Default font">A</button>
              <button class="a11y-choice-btn" data-font="115" style="font-size:17px" aria-label="Large font">A</button>
              <button class="a11y-choice-btn" data-font="130" style="font-size:20px" aria-label="Extra large font">A</button>
            </div>
          </div>

          <div class="a11y-section">
            <div class="a11y-section-label">Contrast</div>
            <div class="a11y-contrast-row">
              <button class="a11y-contrast-btn" data-contrast="normal"   style="background:#fff;color:#000;border-color:#ccc" aria-label="Normal contrast">Normal</button>
              <button class="a11y-contrast-btn" data-contrast="high"     style="background:#000;color:#fff;border-color:#000" aria-label="High contrast">High</button>
              <button class="a11y-contrast-btn" data-contrast="inverted" style="background:#333;color:#eee;border-color:#555" aria-label="Inverted colors">Invert</button>
              <button class="a11y-contrast-btn" data-contrast="dark"     style="background:#111;color:#ccc;border-color:#444" aria-label="Dark mode">Dark</button>
              <button class="a11y-contrast-btn" data-contrast="yellow"   style="background:#000;color:#ff0;border-color:#ff0" aria-label="Yellow on black">Yellow</button>
            </div>
          </div>

          <div class="a11y-section">
            <div class="a11y-slider-row">
              <label class="a11y-slider-label" for="a11y-line-height">Line Height</label>
              <input type="range" id="a11y-line-height" min="1" max="2.5" step="0.1" value="1.5" aria-label="Line height">
            </div>
            <div class="a11y-slider-row">
              <label class="a11y-slider-label" for="a11y-letter-spacing">Letter Spacing</label>
              <input type="range" id="a11y-letter-spacing" min="0" max="0.2" step="0.01" value="0" aria-label="Letter spacing">
            </div>
            <div class="a11y-slider-row">
              <label class="a11y-slider-label" for="a11y-zoom">Page Zoom</label>
              <input type="range" id="a11y-zoom" min="80" max="150" step="5" value="100" aria-label="Page zoom">
            </div>
          </div>

          <div class="a11y-section">
            <div class="a11y-section-label">Text Alignment</div>
            <div class="a11y-font-row">
              <button class="a11y-choice-btn a11y-align-btn" data-align="left" aria-label="Align left">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="18" y2="18"/></svg>
              </button>
              <button class="a11y-choice-btn a11y-align-btn" data-align="center" aria-label="Align center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="5" y1="18" x2="19" y2="18"/></svg>
              </button>
              <button class="a11y-choice-btn a11y-align-btn" data-align="right" aria-label="Align right">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="4" y1="6" x2="20" y2="6"/><line x1="10" y1="12" x2="20" y2="12"/><line x1="6" y1="18" x2="20" y2="18"/></svg>
              </button>
            </div>
          </div>

          <div class="a11y-section">
            <div class="a11y-section-label">Tools</div>
            <div class="a11y-grid">
              <button class="a11y-btn" data-feature="cursor" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4l7 16 2-7 7-2z"/></svg>
                Big Cursor
              </button>
              <button class="a11y-btn" data-feature="links" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1"/><path d="M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1"/></svg>
                Highlight Links
              </button>
              <button class="a11y-btn" data-feature="images" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/><line x1="3" y1="3" x2="21" y2="21"/></svg>
                Hide Images
              </button>
              <button class="a11y-btn" data-feature="animations" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                Stop Animations
              </button>
              <button class="a11y-btn" data-feature="dyslexia" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                Dyslexia Font
              </button>
              <button class="a11y-btn" data-feature="readingMask" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h18M3 12h18M3 17h18"/></svg>
                Reading Mask
              </button>
              <button class="a11y-btn" data-feature="focus" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/></svg>
                Focus Highlight
              </button>
              <button class="a11y-btn" data-feature="tooltips" aria-pressed="false">
                <svg class="a11y-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
                Image Tooltips
              </button>
            </div>
          </div>
        </div>

        <div class="a11y-footer">
          <a class="a11y-declaration-link" href="/pages/accessibility" target="_blank" rel="noopener">Accessibility Statement</a>
        </div>
      </div>
    `;
  }

  // ─── Page wrapping (only when needed) ────────────────────
  function wrapPageContent() {
    if (pageWrapped || document.getElementById('a11y-page-wrap')) return;
    const wrap = document.createElement('div');
    wrap.id = 'a11y-page-wrap';
    const widgetRoot = document.getElementById('a11y-widget-root');
    const skipLink = document.getElementById('a11y-skip-link');
    // Move all body children EXCEPT our widget into the wrap
    const children = Array.from(document.body.childNodes);
    children.forEach(n => {
      if (n !== widgetRoot && n !== skipLink) wrap.appendChild(n);
    });
    // Insert wrap before widget root
    document.body.insertBefore(wrap, widgetRoot);
    pageWrapped = true;
  }

  // ─── Dynamic CSS (line-height, letter-spacing, alignment) ─
  let dynamicStyle = null;
  function injectDynamicCSS() {
    if (!pageWrapped) return; // nothing to target yet
    if (!dynamicStyle) {
      dynamicStyle = document.createElement('style');
      dynamicStyle.id = 'a11y-dynamic';
      document.head.appendChild(dynamicStyle);
    }
    const lh = state.lineHeight;
    const ls = state.letterSpacing;
    const align = state.textAlign;
    // Only inject rules if values differ from defaults — saves work
    const needsLH = lh !== DEFAULTS.lineHeight;
    const needsLS = ls !== DEFAULTS.letterSpacing;
    const needsAlign = !!align;
    if (!needsLH && !needsLS && !needsAlign) {
      dynamicStyle.textContent = '';
      return;
    }
    dynamicStyle.textContent = `
      #a11y-page-wrap,
      #a11y-page-wrap *,
      #a11y-page-wrap p,
      #a11y-page-wrap h1,#a11y-page-wrap h2,#a11y-page-wrap h3,
      #a11y-page-wrap h4,#a11y-page-wrap h5,#a11y-page-wrap h6,
      #a11y-page-wrap li,#a11y-page-wrap td,#a11y-page-wrap span,
      #a11y-page-wrap a,#a11y-page-wrap div,#a11y-page-wrap section,
      #a11y-page-wrap article,#a11y-page-wrap label,#a11y-page-wrap button {
        ${needsLH ? `line-height: ${lh} !important;` : ''}
        ${needsLS ? `letter-spacing: ${ls}em !important;` : ''}
        ${needsAlign ? `text-align: ${align} !important;` : ''}
      }
    `;
  }

  // ─── Apply state ─────────────────────────────────────────
  function applyAll() {
    const body = document.body;
    const html = document.documentElement;

    html.style.fontSize = state.fontSize !== 100 ? state.fontSize + '%' : '';

    const wrap = document.getElementById('a11y-page-wrap');
    if (wrap) wrap.style.zoom = state.zoom !== 100 ? state.zoom + '%' : '';

    injectDynamicCSS();

    ['high','inverted','dark','yellow'].forEach(c => body.classList.remove('a11y-contrast-' + c));
    if (state.contrast !== 'normal') body.classList.add('a11y-contrast-' + state.contrast);

    const featureMap = {
      cursor: 'a11y-big-cursor', links: 'a11y-highlight-links',
      images: 'a11y-hide-images', animations: 'a11y-no-animations',
      dyslexia: 'a11y-dyslexia', focus: 'a11y-focus-highlight',
    };
    Object.entries(featureMap).forEach(([k, cls]) => body.classList.toggle(cls, !!state[k]));

    const mask = document.getElementById('a11y-reading-mask');
    if (mask) mask.classList.toggle('active', !!state.readingMask);

    // Attach/detach mousemove only when needed
    if (state.readingMask && !mouseMoveAttached) {
      document.addEventListener('mousemove', onMouseMoveMask, { passive: true });
      mouseMoveAttached = true;
    } else if (!state.readingMask && mouseMoveAttached) {
      document.removeEventListener('mousemove', onMouseMoveMask);
      mouseMoveAttached = false;
    }

    // Image tooltips: one-time scan only when enabled
    if (state.tooltips) applyImageTooltips();

    updateUI();
    saveState();
  }

  function onMouseMoveMask(e) {
    const mt = document.getElementById('a11y-mask-top');
    const mb = document.getElementById('a11y-mask-bottom');
    if (!mt || !mb) return;
    const y = e.clientY;
    const lh = 50;
    mt.style.cssText = 'top:0;height:' + Math.max(0, y - lh / 2) + 'px';
    mb.style.cssText = 'top:' + (y + lh / 2) + 'px;height:' + window.innerHeight + 'px';
  }

  function applyImageTooltips() {
    document.querySelectorAll('img[alt]').forEach(img => {
      if (!img._a11yTip && img.alt) {
        img._a11yTip = true;
        img.title = img.alt;
      }
    });
  }

  function updateUI() {
    if (!fullyLoaded) return; // panel doesn't exist yet
    document.querySelectorAll('.a11y-choice-btn[data-font]').forEach(btn =>
      btn.classList.toggle('active', parseInt(btn.dataset.font) === state.fontSize));
    document.querySelectorAll('.a11y-contrast-btn').forEach(btn =>
      btn.classList.toggle('active', btn.dataset.contrast === state.contrast));
    document.querySelectorAll('.a11y-choice-btn[data-align]').forEach(btn =>
      btn.classList.toggle('active', btn.dataset.align === state.textAlign));
    document.querySelectorAll('.a11y-btn[data-feature]').forEach(btn => {
      const on = !!state[btn.dataset.feature];
      btn.classList.toggle('active', on);
      btn.setAttribute('aria-pressed', String(on));
    });
    const lh = document.getElementById('a11y-line-height');
    const ls = document.getElementById('a11y-letter-spacing');
    const zm = document.getElementById('a11y-zoom');
    if (lh) lh.value = state.lineHeight;
    if (ls) ls.value = state.letterSpacing;
    if (zm) zm.value = state.zoom;
    const pc = document.getElementById('a11y-persist-chk');
    if (pc) pc.checked = getPersist();
    const tog = document.getElementById('a11y-toggle');
    if (tog) tog.setAttribute('aria-expanded', String(state.open));
  }

  function resetAll() {
    Object.assign(state, DEFAULTS);
    try { localStorage.removeItem(CONFIG.storageKey); } catch {}
    applyAll();
  }

  function closePanel() {
    state.open = false;
    const panel = document.getElementById('a11y-panel');
    const bd = document.getElementById('a11y-backdrop');
    const tog = document.getElementById('a11y-toggle');
    if (panel) panel.classList.remove('open');
    if (bd) bd.classList.remove('visible');
    if (tog) {
      tog.setAttribute('aria-expanded', 'false');
      tog.focus();
    }
  }

  // ─── Lazy load: full UI on first open ────────────────────
  function loadFullUI() {
    if (fullyLoaded) return;

    // 1. Inject full CSS
    const style = document.createElement('style');
    style.id = 'a11y-styles-full';
    style.textContent = getFullCSS();
    document.head.appendChild(style);

    // 2. Wrap page content (needed for filters + zoom)
    wrapPageContent();

    // 3. Build panel HTML
    const root = document.getElementById('a11y-widget-root');
    root.insertAdjacentHTML('beforeend', buildPanelHTML());

    // 4. Attach panel event listeners
    attachPanelEvents();

    fullyLoaded = true;

    // 5. Re-apply state (some things only work after wrap exists)
    applyAll();
  }

  function attachPanelEvents() {
    const $  = id => document.getElementById(id);
    const $$ = s => document.querySelectorAll(s);

    $('a11y-backdrop').addEventListener('click', closePanel);
    $('a11y-close').addEventListener('click', closePanel);
    $('a11y-reset').addEventListener('click', resetAll);

    document.addEventListener('click', e => {
      if (state.open && !$('a11y-panel').contains(e.target) && !$('a11y-toggle').contains(e.target) && e.target.id !== 'a11y-backdrop')
        closePanel();
    });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && state.open) closePanel(); });
    $('a11y-panel').addEventListener('keydown', trapFocus);

    $('a11y-persist-chk').addEventListener('change', e => {
      setPersist(e.target.checked);
      if (!e.target.checked) { try { localStorage.removeItem(CONFIG.storageKey); } catch {} }
      else saveState();
    });

    $$('.a11y-choice-btn[data-font]').forEach(btn =>
      btn.addEventListener('click', () => { state.fontSize = parseInt(btn.dataset.font); applyAll(); }));

    $$('.a11y-contrast-btn').forEach(btn =>
      btn.addEventListener('click', () => { state.contrast = btn.dataset.contrast; applyAll(); }));

    $$('.a11y-choice-btn[data-align]').forEach(btn =>
      btn.addEventListener('click', () => {
        state.textAlign = state.textAlign === btn.dataset.align ? null : btn.dataset.align;
        applyAll();
      }));

    $$('.a11y-btn[data-feature]').forEach(btn =>
      btn.addEventListener('click', () => { state[btn.dataset.feature] = !state[btn.dataset.feature]; applyAll(); }));

    $('a11y-line-height').addEventListener('input', e => { state.lineHeight = parseFloat(e.target.value); applyAll(); });
    $('a11y-letter-spacing').addEventListener('input', e => { state.letterSpacing = parseFloat(e.target.value); applyAll(); });
    $('a11y-zoom').addEventListener('input', e => { state.zoom = parseInt(e.target.value); applyAll(); });

    // Swipe to close on mobile
    const panel = $('a11y-panel');
    let startY = 0, isDragging = false;
    panel.addEventListener('touchstart', e => { startY = e.touches[0].clientY; isDragging = true; }, { passive: true });
    panel.addEventListener('touchmove', e => {
      if (!isDragging) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > 0) panel.style.transform = 'translateY(' + dy + 'px)';
    }, { passive: true });
    panel.addEventListener('touchend', e => {
      isDragging = false;
      const dy = e.changedTouches[0].clientY - startY;
      if (dy > 80) { panel.style.transform = ''; closePanel(); }
      else {
        panel.style.transition = 'transform .2s';
        panel.style.transform = '';
        setTimeout(() => panel.style.transition = '', 200);
      }
    });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const panel = document.getElementById('a11y-panel');
    const els = Array.from(panel.querySelectorAll('button,input,a,[tabindex]:not([tabindex="-1"])')).filter(el => !el.disabled);
    if (!els.length) return;
    const first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openPanel() {
    loadFullUI(); // build everything if not built yet
    state.open = true;
    document.getElementById('a11y-panel').classList.add('open');
    document.getElementById('a11y-backdrop').classList.add('visible');
    document.getElementById('a11y-toggle').setAttribute('aria-expanded', 'true');
    setTimeout(() => {
      const closeBtn = document.getElementById('a11y-close');
      if (closeBtn) closeBtn.focus();
    }, 50);
  }

  // ─── Minimal init: only the FAB + skip link ──────────────
  function init() {
    // Skip link (cheap, accessibility-critical)
    const skipLink = document.createElement('a');
    skipLink.id = 'a11y-skip-link';
    skipLink.href = '#MainContent';
    skipLink.textContent = 'Skip to main content';
    document.body.insertBefore(skipLink, document.body.firstChild);

    // Minimal CSS for the FAB
    const style = document.createElement('style');
    style.id = 'a11y-styles-min';
    style.textContent = MINIMAL_CSS;
    document.head.appendChild(style);

    // FAB button only
    const root = document.createElement('div');
    root.id = 'a11y-widget-root';
    root.innerHTML = `
      <button id="a11y-toggle" aria-label="Accessibility Menu" aria-expanded="false" aria-controls="a11y-panel" title="Accessibility Menu">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="white" stroke-width="1.5"/>
          <circle cx="12" cy="8" r="2" fill="white"/>
          <path d="M12 11v6M9 13h6" stroke="white" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </button>
    `;
    document.body.appendChild(root);

    // Single click handler — opens panel & lazy-loads everything
    document.getElementById('a11y-toggle').addEventListener('click', () => {
      if (state.open) closePanel();
      else openPanel();
    });

    // Load persisted state.
    // If user has saved preferences, we MUST apply them now (e.g. contrast).
    // This forces the full UI to load — but only for returning users
    // with active settings. New visitors keep the fast path.
    loadState();
    if (hasSavedState()) {
      // User had settings saved. Apply them silently.
      // We still defer with rIC to not block LCP.
      schedule(() => {
        loadFullUI(); // wraps page, injects CSS
        applyAll();
      });
    }
  }

  // requestIdleCallback fallback
  function schedule(fn) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 1);
    }
  }

  // ─── Boot ────────────────────────────────────────────────
  // We defer init itself to idle time, so even the FAB injection
  // doesn't compete with LCP. The button appears ~100-500ms after
  // first paint instead of immediately — invisible to users, big
  // gain for Lighthouse.
  function boot() {
    schedule(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
